"use client";

import Image from "next/image";
import Link from "next/link";
import cn from "clsx";

import {
  SUPPORTED_CHAINS,
  SUPPORTED_CHAINS_PATH,
  getChainPath,
} from "@/shared/data/chains";

import s from "./styles.module.css";

/** The nav entry that marks the index page (vs a chain id). */
export const OVERVIEW_ID = "overview";

interface Props {
  /**
   * Which entry is active: a chain id, or OVERVIEW_ID. Supplied by the server
   * component that owns the route, so the active class is identical on the
   * server and client and cannot cause a hydration mismatch.
   */
  activeId: string;
  label: string;
  overviewLabel: string;
}

/**
 * Docs-style sidebar. Each chain is its own page, so the active item comes from
 * the pathname rather than from scroll position. On desktop the nav pins via
 * `position: sticky` (see styles.module.css).
 */
export default function ChainSideNav({
  activeId,
  label,
  overviewLabel,
}: Props) {
  const isOverview = activeId === OVERVIEW_ID;

  return (
    <nav className={s.sideNav} aria-label={label}>
      <span className={s.sideNavLabel}>{label}</span>
      <ul className={s.sideNavList}>
        <li>
          <Link
            href={SUPPORTED_CHAINS_PATH}
            aria-current={isOverview ? "page" : undefined}
            className={cn(
              s.sideNavLink,
              s.sideNavOverview,
              isOverview && s.sideNavLinkActive
            )}
          >
            <span className={s.sideNavName}>{overviewLabel}</span>
          </Link>
        </li>

        {SUPPORTED_CHAINS.map((chain) => {
          const href = getChainPath(chain.id);
          const isActive = chain.id === activeId;

          return (
            <li key={chain.id}>
              <Link
                href={href}
                aria-current={isActive ? "page" : undefined}
                className={cn(s.sideNavLink, isActive && s.sideNavLinkActive)}
                style={
                  {
                    "--chain-accent": chain.accentColor,
                    "--logo-scale": String(chain.logoScale ?? 1),
                  } as React.CSSProperties
                }
              >
                <Image
                  aria-hidden
                  className={s.sideNavLogo}
                  src={chain.logo}
                  alt=""
                  width={20}
                  height={20}
                />
                <span className={s.sideNavName}>{chain.name}</span>
                <span className={s.sideNavSymbol}>{chain.symbol}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
